import _, { keyBy } from 'lodash'

import { getQueryParamId, setQueryParam } from '@app/utils/url-helpers'

import * as mutationTypes from './mutation-types'
import api from '../api/index'

import * as communicationMutationTypes from '@app/application/communication/sections/communication/store/mutation-types'
import { REMARK_ACTIONS } from '@app/application/communication/constants'
import { abort } from '@app/adapters/abort'

function isCompanyAlreadyActive(state, id) {
  return (
    state.notifications.initialDataFetched &&
    id === state.notifications.activeCompanyId
  )
}

export default {
  state() {
    return {
      notifications: {},
      initialUnreadStateById: {},
      companies: {},
      activeNotificationId: getQueryParamId('activeNotificationId'),
      activeCompanyId: getQueryParamId('activeCompanyId'),
      search: '',
      initialDataFetched: false,
    }
  },

  getters: {
    notifications: ({ notifications }) =>
      _.sortBy(Object.values(notifications), 'sortIdx'),
    unreadNotificationCount: ({ notifications }) =>
      Object.values(notifications).filter((n) => !n.read).length,
    companies: ({ companies }) => _.sortBy(Object.values(companies), 'sortIdx'),
    activeNotificationId: ({ activeNotificationId }) => activeNotificationId,
    activeCompanyId: ({ activeCompanyId }) => activeCompanyId,
    notificationCommunicationItem:
      ({ notifications }) =>
      (notificationId) => {
        const notification = notifications[notificationId]

        const id = notification.remarkId || notification.checkLinkId
        const type = notification.remarkId ? 'Remark' : 'CheckLink'

        return { id, type }
      },
    querySearch: ({ search }) => (search === '' ? null : search),
    notificationsByRemarkId: ({ notifications }) => {
      return keyBy(
        Object.values(notifications).filter((n) => n.remarkId),
        'remarkId'
      )
    },
    notificationsByCheckLinkId: ({ notifications }) => {
      return keyBy(
        Object.values(notifications).filter((n) => n.checkLinkId),
        'checkLinkId'
      )
    },
  },
  mutations: {
    [mutationTypes.SET_NOTIFICATION](state, notifications) {
      state.notifications = notifications
      for (const notification of Object.values(notifications)) {
        state.initialUnreadStateById[notification.id] ||= !notification.read
      }
    },
    [mutationTypes.RESET_ACTIVE_NOTIFICATION](state) {
      state.activeNotificationId = null

      setQueryParam('activeNotificationId', undefined)
    },
    [mutationTypes.RESET_NOTIFICATIONS](state) {
      state.notifications = {}
    },
    [mutationTypes.SET_COMPANIES](state, companies) {
      state.companies = companies
    },
    [mutationTypes.SET_ACTIVE_NOTIFICATION](state, id) {
      state.activeNotificationId = id

      setQueryParam('activeNotificationId', id)
    },
    [mutationTypes.SET_ACTIVE_COMPANY](state, { id, replace }) {
      state.activeCompanyId = id
      state.initialUnreadStateById = {}

      setQueryParam('activeCompanyId', id, {
        replace,
      })
    },
    [mutationTypes.RESET_ACTIVE_COMPANY](state) {
      state.activeCompanyId = null

      setQueryParam('activeCompanyId', undefined)
    },
    [mutationTypes.SET_NOTIFICATION_READ_STATE](state, { id, read }) {
      state.notifications[id].read = read
      state.initialUnreadStateById[id] ||= !read
    },
    [mutationTypes.SET_NOTIFICATION_LINKED_COMMENT_ID](
      state,
      { id, linkedCommentId }
    ) {
      state.notifications[id].linkedCommentId = linkedCommentId
    },
    [mutationTypes.SET_COMPANY_NOTIFICATION_COUNT](state, { id, count }) {
      state.companies[id].unreadNotifications = count
    },
    [mutationTypes.SET_SEARCH](state, search) {
      state.search = search
    },
    [mutationTypes.RESET_NOTIFICATION_READ_CACHE](state) {
      for (const notification of Object.values(state.notifications)) {
        state.initialUnreadStateById[notification.id] = !notification.read
      }
    },
    [mutationTypes.RESET_NOTIFICATION_READ_CACHE_FOR_ID](state, id) {
      state.initialUnreadStateById[id] = !state.notifications[id].read
    },
  },
  actions: {
    fetchNotifications(
      { commit, state, getters, dispatch, rootState },
      companyId
    ) {
      commit(mutationTypes.SET_LOADING, { type: 'notifications', value: true })

      return new Promise((resolve) => {
        api
          .getNotifications({
            companyId:
              companyId || state.activeCompanyId || Silverfin.currentCompanyId,
            ledgerId: rootState.ledgerId,
            search: getters.querySearch,
          })
          .then((data) => {
            const notifications = { ..._.keyBy(data, 'id') }

            commit(mutationTypes.SET_NOTIFICATION, notifications)
            commit(mutationTypes.SET_LOADING, {
              type: 'notifications',
              value: false,
            })

            //it handles fetching communicationItem and marking it as read if ID was passed through the URL query params
            if (state.activeNotificationId) {
              const communicationItem = getters.notificationCommunicationItem(
                state.activeNotificationId
              )

              dispatch('fetchCommunicationItem', communicationItem)

              if (!state.notifications[state.activeNotificationId].read) {
                dispatch(
                  'toggleReadNotification',
                  state.activeNotificationId
                ).then(() => {
                  window.eventHub.$emit('resetNotificationCount')
                })
              }
            }

            resolve()
          })
      })
    },

    fetchCompanies({ commit, dispatch, state, getters }, filters = {}) {
      commit(mutationTypes.SET_LOADING, { type: 'companies', value: true })

      filters.search = filters.search || getters.querySearch

      return new Promise((resolve) => {
        api
          .getCompanies({
            filters,
            ignoreChecks: true,
          })
          .then((data) => {
            const activeCompanyId =
              state.activeCompanyId || (data.length > 0 ? data[0].id : null)
            const companies = { ..._.keyBy(data, 'id') }

            if (activeCompanyId !== null) {
              dispatch('setActiveCompany', activeCompanyId)
            }
            commit(mutationTypes.SET_COMPANIES, companies)
            commit(mutationTypes.SET_LOADING, {
              type: 'companies',
              value: false,
            })

            resolve()
          })
      })
    },
    setActiveCompany({ commit, dispatch }, id) {
      if (isCompanyAlreadyActive(this.state, id)) {
        return
      }

      this.state.notifications.initialDataFetched = true

      commit(mutationTypes.SET_ACTIVE_COMPANY, { id, replace: true })
      commit(communicationMutationTypes.RESET_MENTION_USERNAMES)
      commit(mutationTypes.RESET_NOTIFICATIONS)

      abort('communications')
      dispatch('resetCommunicationData')
      dispatch('fetchCommunication')
      dispatch('fetchWorkflows')
      dispatch('fetchNotifications', id)
      dispatch('fetchMentionUsernames')
    },
    handleNotificationClick({ commit, state, dispatch, getters }, id) {
      commit(mutationTypes.SET_ACTIVE_NOTIFICATION, id)

      const communicationItem = getters.notificationCommunicationItem(id)

      dispatch('fetchCommunicationItem', communicationItem)
      if (!state.notifications[id].read) {
        dispatch('toggleReadNotification', id).then(() => {
          window.eventHub.$emit('resetNotificationCount')
        })
      }
    },
    handleFirmNotificationClick({ commit, dispatch }, id) {
      commit(mutationTypes.SET_ACTIVE_COMPANY, { id })
      commit(mutationTypes.RESET_ACTIVE_NOTIFICATION)
      commit(mutationTypes.RESET_COMMUNICATION_ITEM)
      commit(communicationMutationTypes.RESET_MENTION_USERNAMES)

      dispatch('fetchNotifications', id)
      dispatch('fetchMentionUsernames')
    },
    handleNotificationUnread({ state, dispatch }, id) {
      if (state.notifications[id].read) {
        dispatch('toggleReadNotification', id).then(() => {
          window.eventHub.$emit('resetNotificationCount')
        })
      }
    },
    resetActiveNotification({ commit }) {
      commit(mutationTypes.RESET_ACTIVE_NOTIFICATION)
    },
    refreshNotificationData({ commit, dispatch, state, getters }, data) {
      if (data.action === REMARK_ACTIONS.EDITED) {
        const { activeNotificationId } = state
        if (activeNotificationId) {
          const communicationItem =
            getters.notificationCommunicationItem(activeNotificationId)

          dispatch('fetchCommunicationItem', communicationItem)
        }
      } else if (data.action === REMARK_ACTIONS.DESTROYED) {
        commit(mutationTypes.SET_ACTIVE_NOTIFICATION, null)
        commit(mutationTypes.SET_COMMUNICATION_ITEM, { remark: null })

        if (state.activeCompanyId) {
          dispatch('fetchNotifications', state.activeCompanyId)
        } else {
          dispatch('fetchNotifications', Silverfin.currentCompanyId)
        }
      } else if (data.action === REMARK_ACTIONS.CREATED) {
        if (state.activeCompanyId) {
          dispatch('fetchNotifications', state.activeCompanyId)
        } else {
          dispatch('fetchNotifications', Silverfin.currentCompanyId)
        }
      }
    },
    toggleReadNotification({ commit, dispatch }, id) {
      return api.toggleReadNotification({ id }).then((response) => {
        commit(mutationTypes.SET_NOTIFICATION_READ_STATE, {
          id: id,
          read: response.read,
        })
        commit(mutationTypes.SET_NOTIFICATION_LINKED_COMMENT_ID, {
          id: id,
          linkedCommentId: response.linkedCommentId,
        })
        commit(mutationTypes.RESET_NOTIFICATION_READ_CACHE_FOR_ID, id)

        dispatch('resetCompanyNotificationCount')
      })
    },
    resetCompanyNotificationCount({ commit, state, getters }) {
      const companyId = state.activeCompanyId
      if (!_.isEmpty(state.companies)) {
        return new Promise((resolve) => {
          api
            .fetchCompanyUnreadCount({
              companyId,
              search: getters.querySearch,
              ignoreChecks: true,
            })
            .then((response) => {
              commit(mutationTypes.SET_COMPANY_NOTIFICATION_COUNT, {
                id: companyId,
                count: response.count,
              })

              resolve()
            })
        })
      }
    },
    resetNotificationReadCache({ commit }) {
      commit(mutationTypes.RESET_NOTIFICATION_READ_CACHE)
    },
    handleFirmSearch({ commit, dispatch, state }, value) {
      if (state.search === value) return

      commit(mutationTypes.SET_SEARCH, value)

      commit(mutationTypes.RESET_ACTIVE_NOTIFICATION)
      commit(mutationTypes.RESET_ACTIVE_COMPANY)
      commit(mutationTypes.RESET_COMMUNICATION_ITEM)

      dispatch('fetchCompanies')
    },
    handleCompanySearch({ commit, dispatch, state }, value) {
      if (state.search === value) return

      commit(mutationTypes.SET_SEARCH, value)

      commit(mutationTypes.RESET_ACTIVE_NOTIFICATION)
      commit(mutationTypes.RESET_COMMUNICATION_ITEM)

      dispatch('fetchNotifications')
    },
  },
}
